import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface DomainGeneratorResult {
  slug: string | null;
  displayName: string | null;
  isLoading: boolean;
  error: string | null;
  isUnconfiguredDomain: boolean;
  isDevMode: boolean;
  detectedHostname: string;
  isOverridden: boolean;
}

const DEV_DOMAIN_PATTERNS = [
  "localhost",
  "lovableproject.com",
  "lovable.app",
  "127.0.0.1"
];

function isDevDomain(hostname: string): boolean {
  return DEV_DOMAIN_PATTERNS.some(pattern => hostname.includes(pattern));
}

function getGeneratorOverride(): string | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  return params.get("generator");
}

/**
 * Hook to detect which generator to use based on the current domain.
 * Fetches from generator_domains table and falls back to 'swms' if not found.
 * 
 * Features:
 * - URL parameter override: ?generator=slug (dev mode only)
 * - Dev mode detection for localhost/preview domains
 * - Unconfigured domain detection for production domains
 */
export function useDomainGenerator(): DomainGeneratorResult {
  // IMPORTANT: start as unknown so we don't briefly render default SWMS content
  // on production domains that should resolve to another generator.
  const [slug, setSlug] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUnconfiguredDomain, setIsUnconfiguredDomain] = useState(false);
  const [isOverridden, setIsOverridden] = useState(false);

  const hostname = typeof window !== "undefined" ? window.location.hostname : "";
  const isDevMode = isDevDomain(hostname);

  useEffect(() => {
    async function detectGenerator() {
      setIsLoading(true);
      setError(null);
      setIsUnconfiguredDomain(false);
      setIsOverridden(false);

      try {
        // Check for URL parameter override (only in dev mode)
        const override = getGeneratorOverride();
        if (isDevMode && override) {
          // In dev mode, trust the override slug directly.
          // RLS filters inactive generators, so we skip validation to allow
          // testing inactive generators via ?generator=slug in preview.
          console.log(`Using URL override generator: ${override}`);
          setSlug(override);
          setDisplayName(null);
          setIsOverridden(true);
          setIsLoading(false);
          return;
        }

        // For dev/preview domains without override, explicitly default to SWMS.
        // (This avoids blocking the UI in preview environments where domain mapping isn't configured.)
        if (isDevMode) {
          console.log("Preview/dev domain detected, defaulting to SWMS");
          setSlug("swms");
          setDisplayName("SWMS Generator");
          setIsLoading(false);
          return;
        }

        // Query generator_domains for matching domain
        const { data, error: fetchError } = await supabase
          .from("generator_domains")
          .select(`
            domain,
            generator_id,
            generators!inner (
              slug,
              display_name,
              is_active
            )
          `)
          .eq("domain", hostname)
          .eq("generators.is_active", true)
          .single();

        if (fetchError) {
          // No matching domain found
          if (fetchError.code === "PGRST116") {
            // Production domain not configured - flag as unconfigured
            console.warn(`No generator configured for production domain: ${hostname}`);
            setIsUnconfiguredDomain(true);
            setSlug(null);
          } else {
            console.error("Error fetching domain config:", fetchError);
            setError("Failed to detect generator");
            setSlug(null);
          }
          return;
        }

        if (data?.generators) {
          const generators = data.generators as { slug: string; display_name: string; is_active: boolean };
          console.log(`Domain ${hostname} mapped to generator: ${generators.slug}`);
          setSlug(generators.slug);
          setDisplayName(generators.display_name);
        }
      } catch (err) {
        console.error("Failed to detect generator from domain:", err);
        setError(err instanceof Error ? err.message : "Unknown error");
        setSlug(null);
      } finally {
        setIsLoading(false);
      }
    }

    detectGenerator();
  }, [hostname, isDevMode]);

  return { 
    slug, 
    displayName,
    isLoading, 
    error, 
    isUnconfiguredDomain, 
    isDevMode, 
    detectedHostname: hostname,
    isOverridden
  };
}
